import { useEffect, useRef } from 'react';

export enum EHotkey {
  NEXT_LANGUAGE = 'nextLanguage',
  PREVIOUS_LANGUAGE = 'previousLanguage',
  PASTE_NAME = 'pasteName',
  PASTE_DESCRIPTION = 'pasteDescription',
  SUBMIT_FORM = 'submitForm',
  SHOW_HELP = 'showHelp',
}

interface IHotkeyDefinition {
  code: string;
  altKey: boolean;
  shiftKey: boolean;
  legacyKeys: string[];
  label: string;
  description: string;
}

export const HOTKEYS: Record<EHotkey, IHotkeyDefinition> = {
  [EHotkey.NEXT_LANGUAGE]: {
    code: 'KeyS',
    altKey: true,
    shiftKey: false,
    legacyKeys: ['ß'],
    label: 'Alt + S',
    description: 'Switch to the next language',
  },
  [EHotkey.PREVIOUS_LANGUAGE]: {
    code: 'KeyA',
    altKey: true,
    shiftKey: false,
    legacyKeys: ['ą'],
    label: 'Alt + A',
    description: 'Switch to the previous language',
  },
  [EHotkey.PASTE_NAME]: {
    code: 'KeyW',
    altKey: true,
    shiftKey: false,
    legacyKeys: ['ė'],
    label: 'Alt + W',
    description: 'Paste the clipboard into the name (and slug)',
  },
  [EHotkey.PASTE_DESCRIPTION]: {
    code: 'KeyE',
    altKey: true,
    shiftKey: false,
    legacyKeys: ['ę'],
    label: 'Alt + E',
    description: 'Paste the clipboard into the product description',
  },
  [EHotkey.SUBMIT_FORM]: {
    code: 'KeyD',
    altKey: true,
    shiftKey: false,
    legacyKeys: ['∂'],
    label: 'Alt + D',
    description: 'Save the translation form',
  },
  [EHotkey.SHOW_HELP]: {
    code: 'Slash',
    altKey: false,
    shiftKey: true,
    legacyKeys: [],
    label: '?',
    description: 'Show keyboard shortcuts',
  },
};

const TYPING_TAG_NAMES: string[] = ['INPUT', 'TEXTAREA', 'SELECT'];

const isTypingTarget = (target: EventTarget | null): boolean =>
  typeof HTMLElement !== 'undefined' &&
  target instanceof HTMLElement &&
  (target.isContentEditable || TYPING_TAG_NAMES.includes(target.tagName));

export const matchesHotkey = (
  event: KeyboardEvent,
  hotkey: EHotkey,
): boolean => {
  const definition: IHotkeyDefinition = HOTKEYS[hotkey];

  if (definition.legacyKeys.includes(event.key)) {
    return true;
  }

  if (!definition.altKey && isTypingTarget(event.target)) {
    return false;
  }

  return (
    event.code === definition.code &&
    event.altKey === definition.altKey &&
    event.shiftKey === definition.shiftKey &&
    !event.ctrlKey &&
    !event.metaKey
  );
};

export function useHotkey(
  hotkeys: EHotkey[],
  handler: (hotkey: EHotkey, event: KeyboardEvent) => void,
): void {
  const hotkeysRef = useRef(hotkeys);
  const handlerRef = useRef(handler);
  hotkeysRef.current = hotkeys;
  handlerRef.current = handler;

  useEffect((): (() => void) => {
    const listener = (event: KeyboardEvent): void => {
      const hotkey: EHotkey | undefined = hotkeysRef.current.find(
        (candidate: EHotkey): boolean => matchesHotkey(event, candidate),
      );

      if (!hotkey) {
        return;
      }

      event.preventDefault();
      handlerRef.current(hotkey, event);
    };

    document.addEventListener('keydown', listener);

    return (): void => {
      document.removeEventListener('keydown', listener);
    };
  }, []);
}
